import * as React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import Styles from './CostBreakdown.module.css'
import { Colors } from 'Theme'
import { IFText } from 'Components'

const CostBreakdown = ({ costBreakdown, currency }) => {
  const { t } = useTranslation()

  if (!costBreakdown) return null

  const decimals = currency?.decimalPlaces ?? 2
  const formatAmount = (value) =>
    value == null ? '-' : Number(value).toFixed(decimals)
  const formatPoints = (value) =>
    value == null ? '-' : Intl.NumberFormat('en-US').format(value)

  const items = costBreakdown.items ?? []
  const { subtotal, vatTotal, total, pointsTotal } = costBreakdown

  const typeLabelKeys = {
    ENERGY: 'CostBreakdown.TypeEnergy',
    TIME: 'CostBreakdown.TypeTime',
    PARKING_TIME: 'CostBreakdown.TypeParkingTime',
    FLAT: 'CostBreakdown.TypeFlat',
  }
  const getTypeLabel = (type) => {
    const key = typeLabelKeys[type?.toUpperCase?.()]
    return key ? t(key) : type
  }

  const formatVolume = (item) => {
    const volume = item.billedVolume ?? item.volume
    if (volume == null) return '-'
    return Number(volume).toFixed(2)
  }

  const hasMoneyTotals = subtotal != null || vatTotal != null || total != null
  const hasPointsTotal = pointsTotal != null && pointsTotal !== 0

  return (
    <div
      className={Styles.container}
      style={{ color: Colors.text, border: `1px solid ${Colors.DividerColor}` }}
    >
      <IFText className={Styles.title}>{t('CostBreakdown.Title')}</IFText>
      <div className={Styles.table}>
        <div className={[Styles.row, Styles.headerRow].join(' ')}>
          <IFText className={[Styles.cell, Styles.headerCell].join(' ')}>
            {t('CostBreakdown.Type')}
          </IFText>
          <IFText
            className={[Styles.cell, Styles.numberCell, Styles.headerCell].join(
              ' ',
            )}
          >
            {t('CostBreakdown.Volume')}
          </IFText>
          <IFText
            className={[Styles.cell, Styles.numberCell, Styles.headerCell].join(
              ' ',
            )}
          >
            {t('CostBreakdown.ExclVat')}
          </IFText>
          <IFText
            className={[Styles.cell, Styles.numberCell, Styles.headerCell].join(
              ' ',
            )}
          >
            {t('CostBreakdown.InclVat')}
          </IFText>
        </div>

        {items.map((item, idx) => {
          const isPoints = item.paidIn === 'points'
          return (
            <div className={Styles.row} key={`${item.type}-${idx}`}>
              <IFText className={Styles.cell}>{getTypeLabel(item.type)}</IFText>
              <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
                {formatVolume(item)}
              </IFText>
              {isPoints ? (
                <>
                  <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
                    {'-'}
                  </IFText>
                  <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
                    {`${formatPoints(item.pointsAmount)} ${t(
                      'CostBreakdown.Points',
                    )}`}
                  </IFText>
                </>
              ) : (
                <>
                  <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
                    {`${formatAmount(item.exclVat)} ${currency?.code ?? ''}`}
                  </IFText>
                  <IFText className={[Styles.cell, Styles.numberCell].join(' ')}>
                    {`${formatAmount(item.inclVat)} ${currency?.code ?? ''}`}
                  </IFText>
                </>
              )}
            </div>
          )
        })}

        <div className={Styles.totals}>
          {hasMoneyTotals && (
            <>
              <div className={Styles.totalRow}>
                <IFText className={Styles.totalLabel}>
                  {t('CostBreakdown.Subtotal')}
                </IFText>
                <IFText className={Styles.totalValue}>
                  {`${formatAmount(subtotal)} ${currency?.code ?? ''}`}
                </IFText>
              </div>
              <div className={Styles.totalRow}>
                <IFText className={Styles.totalLabel}>
                  {t('CostBreakdown.Vat')}
                </IFText>
                <IFText className={Styles.totalValue}>
                  {`${formatAmount(vatTotal)} ${currency?.code ?? ''}`}
                </IFText>
              </div>
              <div className={Styles.totalRow}>
                <IFText
                  className={[Styles.totalLabel, Styles.grandTotal].join(' ')}
                >
                  {t('CostBreakdown.Total')}
                </IFText>
                <IFText
                  className={[Styles.totalValue, Styles.grandTotal].join(' ')}
                >
                  {`${formatAmount(total)} ${currency?.code ?? ''}`}
                </IFText>
              </div>
            </>
          )}
          {hasPointsTotal && (
            <div className={Styles.totalRow}>
              <IFText
                className={[Styles.totalLabel, Styles.grandTotal].join(' ')}
              >
                {t('CostBreakdown.PointsTotal')}
              </IFText>
              <IFText
                className={[Styles.totalValue, Styles.grandTotal].join(' ')}
              >
                {`${formatPoints(pointsTotal)} ${t('CostBreakdown.Points')}`}
              </IFText>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

CostBreakdown.propTypes = {
  costBreakdown: PropTypes.shape({
    items: PropTypes.array,
    dimensionTotals: PropTypes.array,
    subtotal: PropTypes.number,
    vatTotal: PropTypes.number,
    total: PropTypes.number,
    pointsTotal: PropTypes.number,
  }),
  currency: PropTypes.shape({
    code: PropTypes.string,
    decimalPlaces: PropTypes.number,
  }),
}

export default CostBreakdown
